import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  Crown,
  Medal,
  MapPin,
  Disc3,
  Flame,
  ArrowUp,
  ArrowDown,
  Minus,
  Trophy,
  ChevronLeft,
  ChevronRight,
  Zap,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { PageHero } from "@/components/PageHero";
import { CommunityNavTabs } from "@/components/community/CommunityNavTabs";
import { FramedAvatar } from "@/components/FramedAvatar";
import { ProBadge } from "@/components/ui/pro-badge";
import { getCollectorsRanking, type RankedCollector } from "@/lib/xp.functions";

const PAGE_SIZE = 50;

export const Route = createFileRoute("/colecionadores")({
  head: () => ({
    meta: [
      { title: "Colecionadores — Viny Club" },
      {
        name: "description",
        content:
          "Ranking dos maiores colecionadores de vinil do Viny Club: XP, coleção, sequência e movimentação semanal.",
      },
      { property: "og:title", content: "Colecionadores — Viny Club" },
      {
        property: "og:description",
        content: "Ranking dos maiores colecionadores de vinil do Viny Club.",
      },
    ],
  }),
  component: CollectorsPage,
});

function CollectorsPage() {
  const fn = useServerFn(getCollectorsRanking);
  const { data, isLoading } = useQuery({
    queryKey: ["collectors-ranking"],
    queryFn: () => fn(),
    staleTime: 5 * 60_000,
  });

  const rows = data ?? [];
  const top9 = rows.slice(0, 9);
  const rest = rows.slice(9);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rest.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rest.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [rest, currentPage],
  );

  return (
    <AppShell>
      <div className="lg:hidden">
        <CommunityNavTabs />
      </div>
      <PageHero
        eyebrow="Comunidade"
        title="Top colecionadores"
        subtitle="Ranking semanal por XP. As setas mostram quem subiu, caiu ou manteve a posição desde a semana passada."
      />

      <div className="mx-auto w-full max-w-[1400px] px-4 pb-6 lg:px-5">
        {isLoading ? (
          <SkeletonState />
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Sem colecionadores por aqui ainda.
          </div>
        ) : (
          <>
            <section
              aria-label="Top 9"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            >
              {top9.map((row) => (
                <CollectorCard key={row.user_id} row={row} />
              ))}
            </section>

            {rest.length > 0 && (
              <section aria-label="Ranking completo" className="mt-10">
                <div className="mb-3 flex items-baseline justify-between">
                  <h2 className="font-display text-lg font-semibold">
                    Ranking completo
                  </h2>
                  <span className="text-[11px] text-muted-foreground">
                    {rows.length} colecionadores
                  </span>
                </div>
                <ol className="overflow-hidden rounded-xl border border-border bg-background/50">
                  {pageRows.map((row, i) => (
                    <CollectorRow
                      key={row.user_id}
                      row={row}
                      zebra={i % 2 === 0}
                    />
                  ))}
                </ol>

                {totalPages > 1 && (
                  <nav
                    aria-label="Paginação"
                    className="mt-4 flex items-center justify-between gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" /> Anterior
                    </button>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Próxima <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </nav>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function CollectorCard({ row }: { row: RankedCollector }) {
  const place = row.rank;
  const Icon = place === 1 ? Crown : Medal;
  const tint =
    place === 1
      ? "from-amber-400/25 to-amber-400/0 border-amber-400/40"
      : place === 2
      ? "from-slate-300/20 to-slate-300/0 border-slate-300/30"
      : place === 3
      ? "from-orange-400/20 to-orange-400/0 border-orange-400/30"
      : "from-foreground/[0.05] to-foreground/[0.01] border-foreground/10";
  const iconColor =
    place === 1
      ? "text-amber-400"
      : place === 2
      ? "text-slate-300"
      : place === 3
      ? "text-orange-400"
      : "text-foreground/50";

  return (
    <Link
      to="/profile/$username"
      params={{ username: row.username }}
      className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-b p-4 transition-all hover:-translate-y-0.5 hover:border-foreground/30 ${tint}`}
    >
      {/* Header: rank + delta on the left, Pro badge on the right — never overlap */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1 text-xs font-bold">
          <Icon className={`h-4 w-4 ${iconColor}`} />
          <span className={iconColor}>#{place}</span>
          <RankDeltaBadge delta={row.rank_delta} />
        </div>
        {row.is_premium && <ProBadge size="xs" />}
      </div>

      <div className="flex items-center gap-3">
        <FramedAvatar
          userId={row.user_id}
          src={row.avatar_url}
          username={row.username}
          isPremium={!!row.is_premium}
          rank={row.rank}
          size={64}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-base leading-tight">
            {row.display_name || row.username}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            @{row.username}
          </div>
          {row.city && (
            <div className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{row.city}</span>
            </div>
          )}
        </div>
      </div>

      {row.covers && row.covers.length > 0 && (
        <div className="mt-3 grid grid-cols-4 gap-1.5">
          {row.covers.slice(0, 4).map((c, i) => (
            <div key={i} className="aspect-square overflow-hidden rounded-md ring-1 ring-foreground/10">
              <img
                src={c}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between border-t border-foreground/10 pt-2.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1" title={`${row.owned_count} discos`}>
          <Disc3 className="h-3.5 w-3.5" />
          <span className="tabular-nums text-foreground">{row.owned_count}</span>
        </span>
        <span
          className="inline-flex items-center gap-1"
          title={`${row.current_streak ?? 0} dias de sequência`}
        >
          <Flame className="h-3.5 w-3.5 text-orange-400" />
          <span className="tabular-nums text-foreground">
            {row.current_streak ?? 0}
          </span>
        </span>
        <span className="inline-flex items-center gap-1" title={`${row.xp} XP`}>
          <Zap className="h-3.5 w-3.5 text-amber-400" />
          <span className="font-display text-sm font-bold text-foreground tabular-nums">
            {row.xp}
          </span>
        </span>
      </div>
    </Link>
  );
}

function CollectorRow({ row, zebra }: { row: RankedCollector; zebra: boolean }) {
  return (
    <li
      className={`flex items-center gap-3 border-b border-border/60 px-3 py-2 last:border-b-0 ${
        zebra ? "bg-background/40" : ""
      }`}
    >
      <div className="flex w-14 shrink-0 items-center gap-1 text-xs font-semibold tabular-nums">
        <span className="w-6 text-right text-muted-foreground">{row.rank}</span>
        <RankDeltaBadge delta={row.rank_delta} compact />
      </div>

      <Link
        to="/profile/$username"
        params={{ username: row.username }}
        className="flex min-w-0 flex-1 items-center gap-2.5"
      >
        <FramedAvatar
          userId={row.user_id}
          src={row.avatar_url}
          username={row.username}
          isPremium={!!row.is_premium}
          rank={row.rank}
          size={32}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 truncate text-sm font-medium">
            <span className="truncate">{row.display_name || row.username}</span>
            {row.is_premium && <ProBadge size="xs" />}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">
            @{row.username}
            {row.city ? ` · ${row.city}` : ""}
          </div>
        </div>
      </Link>

      <div className="flex shrink-0 items-center gap-3 text-[11px] text-muted-foreground">
        <span className="hidden items-center gap-1 md:inline-flex" title="Discos">
          <Disc3 className="h-3 w-3" />
          <span className="tabular-nums text-foreground">{row.owned_count}</span>
        </span>
        <span className="hidden items-center gap-1 md:inline-flex" title="Sequência">
          <Flame className="h-3 w-3 text-orange-400" />
          <span className="tabular-nums text-foreground">
            {row.current_streak ?? 0}
          </span>
        </span>
        <span className="inline-flex items-center gap-1" title="XP">
          <Zap className="h-3 w-3 text-amber-400" />
          <span className="font-display text-sm font-semibold text-foreground tabular-nums">
            {row.xp}
          </span>
        </span>
      </div>
    </li>
  );
}

function RankDeltaBadge({
  delta,
  compact = false,
}: {
  delta: number | null;
  compact?: boolean;
}) {
  if (delta == null) {
    return (
      <span
        className={`inline-flex items-center text-muted-foreground/70 ${
          compact ? "" : "ml-1"
        }`}
        title="Sem histórico"
      >
        <Minus className="h-3 w-3" />
      </span>
    );
  }
  if (delta > 0) {
    return (
      <span
        className={`inline-flex items-center gap-0.5 text-emerald-500 ${
          compact ? "" : "ml-1"
        }`}
        title={`Subiu ${delta} posição${delta > 1 ? "s" : ""}`}
      >
        <ArrowUp className="h-3 w-3" />
        <span className="text-[10px] font-bold tabular-nums">{delta}</span>
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span
        className={`inline-flex items-center gap-0.5 text-red-500 ${
          compact ? "" : "ml-1"
        }`}
        title={`Caiu ${Math.abs(delta)} posição${Math.abs(delta) > 1 ? "s" : ""}`}
      >
        <ArrowDown className="h-3 w-3" />
        <span className="text-[10px] font-bold tabular-nums">
          {Math.abs(delta)}
        </span>
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center text-muted-foreground ${
        compact ? "" : "ml-1"
      }`}
      title="Manteve a posição"
    >
      <Minus className="h-3 w-3" />
    </span>
  );
}

function SkeletonState() {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <div
            key={i}
            className="h-40 animate-pulse rounded-2xl border border-border bg-surface/40"
          />
        ))}
      </div>
      <div className="mt-8 space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-lg border border-border bg-surface/30"
          />
        ))}
      </div>
    </>
  );
}
